import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Award, Check, Megaphone, User, Users } from "lucide-react";
import { Reveal, Overline } from "@/components/Motion";
import {
  IMAGES,
  FORMATIONS_PRESENTIEL,
  FORMATIONS_DISTANCIEL,
  PRESENTIEL_INCLUDES,
  eur,
} from "@/data/maison";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POURQUOI = [
  {
    icon: User,
    title: "100 % individuel",
    text: "En présentiel, vous êtes seule avec Oriane : un one-to-one intégral, du premier tracé au certificat.",
  },
  {
    icon: Users,
    title: "Modèles réels",
    text: "Vous pratiquez sur de vrais modèles, dans les conditions réelles du studio.",
  },
  {
    icon: Award,
    title: "Certificat Maison Ria",
    text: "Remis à l'issue de chaque formation, avec la liste de matériel offerte pour vous équiper.",
  },
  {
    icon: Megaphone,
    title: "Bonus réseaux sociaux",
    text: "Un module communication & réseaux sociaux offert, pour lancer et faire vivre votre activité.",
  },
];

const prix = (f) => (f.price === 0 ? "Offert" : eur(f.price));

export default function Academie() {
  const [form, setForm] = useState({
    nom: "",
    email: "",
    telephone: "",
    formation: FORMATIONS_PRESENTIEL[0].id,
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSent(true);
      toast.success("Candidature envoyée. Nous revenons vers vous sous 48 h.");
    } catch {
      toast.error("Une erreur est survenue, merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const candidater = (id) => {
    setForm((f) => ({ ...f, formation: id }));
    document.getElementById("candidature")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 pt-16 md:pt-24 pb-16 grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <Reveal y={16}>
            <Overline>L'Académie — Présentiel & en ligne</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display font-light tracking-tighter text-5xl md:text-6xl lg:text-7xl leading-[0.95]">
              Faites de la dermopigmentation votre{" "}
              <span className="font-script text-[1.1em] text-primary">métier</span>.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-lg text-base md:text-lg font-light leading-relaxed text-muted-foreground">
              La méthode Maison Ria, transmise en one-to-one au studio ou en
              ligne : technique, hygiène, colorimétrie — et les clés pour lancer
              votre activité.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.25} className="lg:col-span-5">
          <div className="overflow-hidden border border-border">
            <img
              src={IMAGES.academy}
              alt="L'Académie Maison Ria — cadre de formation"
              className="w-full aspect-[4/3] object-cover"
            />
          </div>
        </Reveal>
      </section>

      {/* Présentiel */}
      <section className="border-y border-border bg-white/60">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-24" data-testid="academie-presentiel">
          <Reveal>
            <Overline>Au studio — Paris 17ᵉ</Overline>
            <h2 className="mt-6 font-display font-light tracking-tight text-4xl md:text-5xl">
              Les formations en présentiel
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <ul className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
              {PRESENTIEL_INCLUDES.map((inc) => (
                <li key={inc} className="flex items-center gap-3 text-sm font-light">
                  <Check size={15} strokeWidth={1.5} className="text-accent shrink-0" />
                  {inc}
                </li>
              ))}
            </ul>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FORMATIONS_PRESENTIEL.map((f, i) => (
              <Reveal key={f.id} delay={(i % 3) * 0.08}>
                <article className="border border-border bg-background p-8 h-full flex flex-col transition-shadow duration-300 hover:shadow-[0_20px_40px_rgba(46,33,21,0.07)]">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-accent font-semibold">
                    Présentiel · One-to-one
                  </span>
                  <h3 className="mt-4 font-display text-2xl font-light leading-snug">{f.name}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{f.duration}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{f.suivi}</p>
                  <div className="mt-8 pt-6 border-t border-border mt-auto flex items-center justify-between">
                    <span className="font-display text-2xl">{prix(f)}</span>
                    <button
                      data-testid={`academie-cta-${f.id}`}
                      onClick={() => candidater(f.id)}
                      className="text-xs tracking-[0.16em] uppercase border border-foreground px-5 py-3 transition-colors duration-300 hover:bg-foreground hover:text-background"
                    >
                      Candidater
                    </button>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Distanciel */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-24" data-testid="academie-distanciel">
        <Reveal>
          <Overline>À distance</Overline>
          <h2 className="mt-6 font-display font-light tracking-tight text-4xl md:text-5xl">
            Les formations en ligne
          </h2>
          <p className="mt-6 max-w-xl font-light text-muted-foreground">
            Toutes nos formations à distance sont en format vidéo, accompagnées
            de documents et d'e-books.
          </p>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FORMATIONS_DISTANCIEL.map((f, i) => (
            <Reveal key={f.id} delay={(i % 3) * 0.08}>
              <article className="border border-border bg-white p-8 h-full flex flex-col transition-shadow duration-300 hover:shadow-[0_20px_40px_rgba(46,33,21,0.07)]">
                <span className="text-[10px] tracking-[0.2em] uppercase text-accent font-semibold">
                  En ligne
                </span>
                <h3 className="mt-4 font-display text-xl font-light leading-snug">{f.name}</h3>
                <p className="mt-2 text-xs text-muted-foreground">{f.duration}</p>
                <p className="mt-4 text-sm font-light text-muted-foreground leading-relaxed flex-1">
                  {f.desc}
                </p>
                <div className="mt-8 pt-6 border-t border-border flex items-center justify-between">
                  <span className="font-display text-xl">{prix(f)}</span>
                  <button
                    data-testid={`academie-cta-${f.id}`}
                    onClick={() => candidater(f.id)}
                    className="text-xs tracking-[0.16em] uppercase border border-foreground px-4 py-3 transition-colors duration-300 hover:bg-foreground hover:text-background"
                  >
                    Candidater
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Pourquoi */}
      <section className="border-y border-border bg-white/60">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <Overline>La pédagogie Maison Ria</Overline>
            <h2 className="mt-6 font-display font-light tracking-tight text-4xl md:text-5xl max-w-2xl leading-tight">
              On n'apprend pas un geste d'exception dans une salle de trente.
            </h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {POURQUOI.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="border border-border p-8 h-full bg-background">
                  <p.icon size={26} strokeWidth={1} className="text-accent" />
                  <h3 className="mt-4 font-display text-xl">{p.title}</h3>
                  <p className="mt-2 text-sm font-light text-muted-foreground leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Candidature */}
      <section id="candidature" className="bg-primary text-primary-foreground scroll-mt-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline className="text-primary-foreground/60">Candidature</Overline>
              <h2 className="mt-6 font-display font-light tracking-tight text-4xl md:text-5xl leading-tight">
                Racontez-nous votre projet.
              </h2>
              <p className="mt-8 font-light text-primary-foreground/70 leading-relaxed max-w-md">
                Chaque candidature est lue personnellement par Oriane. Réponse
                sous 48 h, entretien téléphonique avant toute inscription.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-7">
            {sent ? (
              <div className="border border-accent/50 p-10" data-testid="academy-success">
                <p className="font-display text-3xl font-light">Candidature reçue.</p>
                <p className="mt-4 font-light text-primary-foreground/70">
                  Merci {form.nom.split(" ")[0]} — vous recevrez une réponse à{" "}
                  <span className="text-accent">{form.email}</span> sous 48 h.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="academy-form" className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
                <input
                  data-testid="academy-input-nom"
                  required
                  placeholder="Nom complet"
                  value={form.nom}
                  onChange={(e) => setForm({ ...form, nom: e.target.value })}
                  className="field-input !border-primary-foreground/30 !text-primary-foreground placeholder:!text-primary-foreground/50 focus:!border-accent"
                />
                <input
                  data-testid="academy-input-email"
                  required
                  type="email"
                  placeholder="Adresse e-mail"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="field-input !border-primary-foreground/30 !text-primary-foreground placeholder:!text-primary-foreground/50 focus:!border-accent"
                />
                <input
                  data-testid="academy-input-telephone"
                  type="tel"
                  placeholder="Téléphone (optionnel)"
                  value={form.telephone}
                  onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                  className="field-input !border-primary-foreground/30 !text-primary-foreground placeholder:!text-primary-foreground/50 focus:!border-accent"
                />
                <select
                  data-testid="academy-select-formation"
                  value={form.formation}
                  onChange={(e) => setForm({ ...form, formation: e.target.value })}
                  className="field-input !border-primary-foreground/30 !text-primary-foreground focus:!border-accent bg-transparent"
                >
                  <optgroup label="Présentiel — au studio" className="text-foreground">
                    {FORMATIONS_PRESENTIEL.map((f) => (
                      <option key={f.id} value={f.id} className="text-foreground">
                        {f.name} — {prix(f)}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="En ligne" className="text-foreground">
                    {FORMATIONS_DISTANCIEL.map((f) => (
                      <option key={f.id} value={f.id} className="text-foreground">
                        {f.name} — {prix(f)}
                      </option>
                    ))}
                  </optgroup>
                </select>
                <textarea
                  data-testid="academy-input-message"
                  rows={4}
                  placeholder="Votre parcours, votre projet (optionnel)"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="field-input sm:col-span-2 !border-primary-foreground/30 !text-primary-foreground placeholder:!text-primary-foreground/50 focus:!border-accent resize-none"
                />
                <div className="sm:col-span-2">
                  <button
                    data-testid="academy-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="bg-primary-foreground text-primary text-sm tracking-[0.14em] uppercase px-10 py-4 transition-colors duration-300 hover:bg-accent disabled:opacity-60"
                  >
                    {loading ? "Envoi en cours…" : "Envoyer ma candidature"}
                  </button>
                </div>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}
